import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { requestGuide } from "@/lib/lead-magnet.functions";
import { trackCta, trackConversion, trackFunnelStep, track } from "@/lib/analytics";
import { Button } from "./Button";
import { useLocale } from "@/i18n/LocaleProvider";

type Mode = "download" | "notify";

// Lead magnet form: captures email -> logs to Lovable Cloud -> triggers PDF download
// (mode = "download") or just confirms upcoming release (mode = "notify").
export function LeadMagnetForm({
  location = "kunskap_lead_magnet",
  variant = "section",
  id,
  magnetSlug = "formatguide-2026",
  mode = "download",
  title,
  description,
  ctaLabel,
  compact = false,
}: {
  location?: string;
  variant?: "section" | "inline";
  id?: string;
  magnetSlug?: string;
  mode?: Mode;
  title?: string;
  description?: string;
  ctaLabel?: string;
  compact?: boolean;
}) {
  const en = useLocale() === "en";
  const submit = useServerFn(requestGuide);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadFilename, setDownloadFilename] = useState<string | null>(null);
  const [resultTitle, setResultTitle] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state === "loading") return;
    trackCta("download_lead_magnet", {
      location,
      label: title ?? magnetSlug,
    });
    trackFunnelStep("lead_magnet_request", { location, magnet: magnetSlug });
    try {
      const sourcePath = typeof window !== "undefined" ? window.location.pathname : null;
      const ua = typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null;
      const { getAttribSnapshot } = await import("@/lib/analytics/track");
      const attrib = getAttribSnapshot();
      const result = await submit({
        data: {
          magnet_slug: magnetSlug,
          email,
          company: company || null,
          source_path: sourcePath,
          user_agent: ua,
          utm_source: attrib.utm_source,
          utm_medium: attrib.utm_medium,
          utm_campaign: attrib.utm_campaign,
          session_id: attrib.session_id,
          visitor_id: attrib.visitor_id,
        },
      });
      setDownloadUrl(result.url);
      setDownloadFilename(result.filename);
      setResultTitle(result.title);
      setState("done");
      trackConversion({
        conversion_type:
          result.status === "released" ? "lead_magnet_download" : "lead_magnet_notify",
        location,
        label: result.title,
      });
      if (result.status === "released" && result.url && typeof window !== "undefined") {
        const a = document.createElement("a");
        a.href = result.url;
        if (result.filename) a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (err) {
      console.error(err);
      track("lead_magnet_error", { location, magnet: magnetSlug });
      setState("error");
    }
  }

  const padding = compact ? "p-5" : variant === "inline" ? "p-6" : "p-8";

  if (state === "done") {
    const isDownload = !!downloadUrl;
    return (
      <div id={id} className={`rounded-3xl border border-mint/40 bg-surface ${padding}`}>
        <div className="text-xs uppercase tracking-[0.18em] text-mint">
          {en ? "Done" : "Klart"}
        </div>
        <h3 className="mt-2 text-xl font-semibold md:text-2xl">
          {isDownload
            ? en
              ? `${resultTitle ?? "Your guide"} is on its way down.`
              : `${resultTitle ?? "Guiden"} är på väg ner.`
            : en
              ? `We'll email you when ${resultTitle ?? "the guide"} drops.`
              : `Vi mailar dig så fort ${resultTitle ?? "guiden"} släpps.`}
        </h3>
        {isDownload && (
          <p className="mt-3 text-sm text-muted-foreground">
            {en ? "Download didn't start automatically? " : "Startar nedladdningen inte automatiskt? "}
            <a href={downloadUrl ?? "#"} download={downloadFilename ?? undefined} className="underline text-mint">
              {en ? "Grab it here" : "Hämta den här"}
            </a>
            .
          </p>
        )}
        {!isDownload && (
          <p className="mt-3 text-sm text-muted-foreground">
            {en
              ? "We'll send a single email — no newsletter, no spam."
              : "Vi skickar ett enda mail — inget nyhetsbrev, ingen spam."}
          </p>
        )}
      </div>
    );
  }

  const defaultTitle =
    mode === "download"
      ? en
        ? `${title ?? "Free guide"} — enter your email to download`
        : `${title ?? "Gratis guide"} — skriv in mejl för nedladdning`
      : en
        ? `Get notified when ${title ?? "this guide"} drops`
        : `Bli notifierad när ${title ?? "guiden"} släpps`;

  const defaultDesc =
    description ??
    (mode === "download"
      ? en
        ? "We'll email the PDF to you and start the download immediately. No newsletter."
        : "Vi mailar PDF:en och startar nedladdningen direkt. Inget nyhetsbrev."
      : en
        ? "One email when the guide is published. Nothing else."
        : "Ett mail när guiden släpps. Inget mer.");

  return (
    <form data-track-form="lead-magnet"
      id={id}
      onSubmit={onSubmit}
      className={`rounded-3xl border border-white/10 bg-surface ${padding}`}
    >
      <div className="text-xs uppercase tracking-[0.18em] text-mint">
        {mode === "download"
          ? en ? "Free download" : "Gratis nedladdning"
          : en ? "Notify me" : "Notifiera mig"}
      </div>
      <h3
        className={`mt-2 font-semibold leading-tight ${
          compact ? "text-lg md:text-xl" : variant === "inline" ? "text-xl md:text-2xl" : "text-2xl md:text-3xl"
        }`}
      >
        {defaultTitle}
      </h3>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">{defaultDesc}</p>

      <div className="mt-5 grid gap-3 md:grid-cols-2">
        <input
          type="email"
          required
          placeholder={en ? "your@email.com" : "din@mejl.se"}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-xl border border-white/10 bg-background px-4 py-3 text-sm outline-none focus:border-mint"
        />
        <input
          type="text"
          placeholder={en ? "Company (optional)" : "Företag (valfritt)"}
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="rounded-xl border border-white/10 bg-background px-4 py-3 text-sm outline-none focus:border-mint"
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button type="submit" variant="mint" arrow disabled={state === "loading"}>
          {state === "loading"
            ? en ? "Sending…" : "Skickar…"
            : ctaLabel ??
              (mode === "download"
                ? en ? "Send me the guide" : "Skicka mig guiden"
                : en ? "Notify me" : "Notifiera mig")}
        </Button>
        <span className="text-xs text-muted-foreground">
          {en
            ? "We won't email you anything unless you ask. No spam, promise."
            : "Vi mailar bara om guiden och en ev. uppföljning. Ingen spam."}
        </span>
      </div>

      {state === "error" && (
        <p className="mt-4 text-sm text-peach">
          {en
            ? "Something went wrong. Please try again in a moment or email hey@fenny.se."
            : "Något gick fel. Försök igen om en stund eller mejla hey@fenny.se."}
        </p>
      )}
    </form>
  );
}
